/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { inject } from "mobx-react";
import { discovery } from "dc-core";
import PropTypes from "prop-types";
import FTECoachmarkStyles from "./FTECoachmark.css";
import { waitForDOMElement } from "dc-preview-utils/src/Utils/Common";
import {
  FTESelectors,
  FTE_COACHMARK_PARENT_ID,
  TARGET_PRODUCTIVITY_CONTEXT_MENU,
  VERB_COMMENT_FTE
} from "../../../constants/enums";
import { trackCoachMarkAccepted, trackCoachMarkDismissed } from "analytics/events";

const executeExitCallback = () => callDropinMethod(VERB_COMMENT_FTE, "exitCallback");
const callDropinMethod = (cohortId, methodName, opts) => discovery.callBootstrapMethod(cohortId, methodName, opts);

const DISMISS_TIME = 15 * 1000;

const FTECoachmark = props => {
  const cohortId = props.showFTE.cohortId;
  const selector = cohortId && FTESelectors[cohortId] ? FTESelectors[cohortId] : null;
  const setShowFTE = props.setShowFTE;
  const setShouldShowInteractiveTooltips = props.setShouldShowInteractiveTooltips;
  const [{ CoachMark }, setCoachmark] = useState({ CoachMark: null });
  const timeoutRef = useRef(null);

  useEffect(() => {
    /**
     * If the CoachMark is already fetched and set in state,
     * don't fetch it again.
     */
    if (!CoachMark && selector && cohortId) {
      callDropinMethod(VERB_COMMENT_FTE, "executeAction", { cohortId })
        .then(resp => {
          const { CoachMark, setFocusForPageLevelComment } = resp;
          waitForDOMElement(`#${selector}`, 2000)
            .then(() => {
              setCoachmark({ CoachMark });
              setFocusForPageLevelComment();
            })
            .catch(e => {
              // Do nothing;
              console.log(e);
            });
        })
        .catch(e => {
          // Do nothing;
          console.log(e);
        });
    }
  }, [cohortId, selector, CoachMark]);

  useEffect(() => {
    /**
     * If CoachMark is rendered and no timer is set.
     * Set a timer of 10 seconds.
     */
    if (CoachMark && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        setShowFTE({ cohortId: null });
        setShouldShowInteractiveTooltips(true);
        timeoutRef.current = null;
        executeExitCallback({ cohortId });
        trackCoachMarkDismissed(cohortId);
      }, DISMISS_TIME);
    }
  }, [CoachMark, setShowFTE, setShouldShowInteractiveTooltips]);

  const onConfirm = () => {
    setShowFTE({ verbId: null });
    setShouldShowInteractiveTooltips(true);
    executeExitCallback({ cohortId });
    trackCoachMarkAccepted(cohortId);
    clearTimeout(timeoutRef.current);
  };

  /**
   * Instead of rendering FTE CoachMark inside Preview,
   * we are rendering it inside a div in Viewer using React.createPortal API.
   * This is because Preview's main parent has position set to relative,
   * and that creates issues with CoachMark positioning.
   */
  let coachMarkElement = null;
  if (CoachMark && selector) {
    coachMarkElement = ReactDOM.createPortal(
      <CoachMark selector={`#${selector}`} onConfirm={onConfirm} fteAction={cohortId} {...props} />,
      document.querySelector(`#${FTE_COACHMARK_PARENT_ID}`)
    );
  }

  return (
    <div id={FTESelectors[TARGET_PRODUCTIVITY_CONTEXT_MENU]} className={FTECoachmarkStyles.FTECoachmarkContainer}>
      {coachMarkElement}
    </div>
  );
};

FTECoachmark.propTypes = {
  showFTE: PropTypes.shape({
    cohortId: PropTypes.string
  }).isRequired,
  setShowFTE: PropTypes.func.isRequired,
  setShouldShowInteractiveTooltips: PropTypes.func.isRequired
};

const mapStoretoProps = stores => ({
  showFTE: stores.appStore.showFTE,
  setShowFTE: stores.appStore.setShowFTE,
  setShouldShowInteractiveTooltips: stores.appStore.setShouldShowInteractiveTooltips
});

export default inject(mapStoretoProps)(FTECoachmark);
